import { getValueByPath } from "shared/app/VerificationTemplate/utils";
import { toMoney } from "shared/utils";
import { CombinedIncome } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/income";
import { VerifiedItem } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified";
import { VerifiedBankStatement } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified/bank-statment";
import { IncomeField } from "./types";
import {
  earliestDate,
  inclusiveDaysBetween,
  latestDate,
  parseDate
} from "./utils";

// Mirrors BoomScreen::CombinedIncome::Income::BANK_STATEMENT_GROSS_MULTIPLIER
const BANK_STATEMENT_GROSS_MULTIPLIER = 1.2;

type BankStatementMap = Record<number, VerifiedBankStatement>;

const getTransactionDate = (item: VerifiedItem) =>
  parseDate(
    getValueByPath<string | undefined>(item, ["data", "transaction", "date"])
  );

/**
 * Deposits across the verified statement period are annualized to a net monthly figure.
 * Gross is estimated from deposits with a flat multiplier.
 */
const bankStatementFormula = (
  income: CombinedIncome,
  items: VerifiedItem[],
  field: IncomeField,
  bankStatements: BankStatementMap
): string | null => {
  const sourceIds = [...new Set(items.map((item) => item.income_source_id))];
  const statements = sourceIds
    .map((sourceId) => bankStatements[sourceId])
    .filter((statement): statement is VerifiedBankStatement =>
      Boolean(statement)
    );
  if (!statements.length) return null;

  const totalDeposits = statements
    .filter((statement) => statement?.additional_data?.is_fraudulent !== true)
    .reduce(
      (sum, statement) => sum + (statement.totals?.[income.id]?.net_total ?? 0),
      0
    );

  const allStatementDates = statements.flatMap((statement) => [
    parseDate(statement.additional_data?.bank_statement_start_date),
    parseDate(statement.additional_data?.bank_statement_end_date),
    parseDate(statement.additional_data?.earliest_transaction_date),
    parseDate(statement.additional_data?.latest_transaction_date)
  ]);
  const transactionDates = items
    .filter(
      (item) =>
        bankStatements[item.income_source_id]?.additional_data
          ?.is_fraudulent !== true
    )
    .map(getTransactionDate);

  const allDates = [...allStatementDates, ...transactionDates];
  const daysBetween = inclusiveDaysBetween(
    earliestDate(allDates),
    latestDate(allDates)
  );
  if (!totalDeposits || !daysBetween) return null;

  const grossMultiplier =
    field === "gross" ? `*${BANK_STATEMENT_GROSS_MULTIPLIER}` : "";

  return `(${toMoney(totalDeposits)}/${daysBetween} days)*(365/12)${grossMultiplier}`;
};

export const BankStatementTooltip = ({
  income,
  items,
  field,
  bankStatements,
  displayedMonthly,
  isManualApplicant
}: {
  income: CombinedIncome;
  items: VerifiedItem[];
  field: IncomeField;
  bankStatements: BankStatementMap;
  displayedMonthly: number;
  isManualApplicant: boolean;
}) => {
  // Verification does not run for bank statements in manual apps
  if (isManualApplicant) return null;

  const formula = bankStatementFormula(income, items, field, bankStatements);
  if (!formula) return null;

  return (
    <>
      Verified {field} monthly calculation
      <br />
      {formula} = {toMoney(displayedMonthly)}
    </>
  );
};
